import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuditService } from 'src/app/core/audit-management/audit.service';
import { AuditLog } from 'src/app/shared/models/AuditLog';

interface FieldChange {
  field: string;
  label: string;
  before: any;
  after: any;
}

interface FebHistoryItem {
  log: AuditLog;
  date: Date;
  projet?: string;
  statutAvant?: string;
  statutApres?: string;
  changes: FieldChange[];
}

@Component({
  selector: 'app-feb-history',
  templateUrl: './feb-history.component.html',
  styleUrl: './feb-history.component.less'
})
export class FebHistoryComponent {

  isLoading = true;
  items: FebHistoryItem[] = [];
  febLabel = '';

  // ── Labels lisibles pour tous les champs du modèle FEB ───────────────────
  private fieldLabels: Record<string, string> = {
    // Champs principaux
    febId:                           null!,   // ID technique → masqué
    febNumber:                       'N° FEB',
    febLabel:                        'Label de la FEB',
    fibLabel:                        'Label de la FEB',
    febStatut:                       'Statut',
    febPcSncf:                       'PC SNCF fourni',
    febDateDemande:                  'Date de la demande',
    febDateDemarrage:                'Date de démarrage',
    febDateFinPrevisionnelle:        'Date fin prévisionnelle',
    febDuree:                        'Durée d\'intervention',
    febNomenclature:                 'Nomenclature',
    febPresenceSiteSncf:             'Présence site SNCF / Télétravail',
    febProvisionDeplacementAstreinte:'Provision déplacement & astreinte',
    febContexteObjectifProjet:       'Contexte et objectif du projet',
    febDescriptionBesoin:            'Description du besoin',
    febNbAnneesExperiences:          'Années d\'expérience',
    febCompetencesFonctionnelles:    'Compétences fonctionnelles',
    febCompetencesTechniques:        'Compétences techniques',
    febEnvironnementTechnique:       'Environnement technique',
    febSavoirEtreQualites:           'Savoir-être et qualités',

    // Relations (IDs → masqués, noms → lisibles)
    febPilotLotId:                   null!,
    febPilotLotNom:                  'Pilote de lot',
    febDemandeurId:                  null!,
    febDemandeurNom:                 'Demandeur',
    febEntiteSncfId:                 null!,
    febEntiteSncfNom:                'Entité SNCF',
    febDivisionDemandeurId:          null!,
    febDivisionDemandeurNom:         'Division du demandeur',
    febProjectId:                    null!,
    febProjectNom:                   'Projet',
    febLocalisationId:               null!,
    febLocalisationLabel:            'Localisation de mission',
    febUoActivitePressentiId:        null!,
    febUoActivitePressentiLabel:     'Activité UO',
    febUoNomenclatureLabel:          'Nomenclature UO',
    febCreatedById:                  null!,
    febCreatedByNom:                 'Créé par',
    febDemandReference:              'N° Demande liée',

    // Timestamps → masqués
    createdAt:                       null!,
    updatedAt:                       null!,

    // Champs d'activité (sous-champs)
    label:                           'Label',
    pourcentage:                     'Répartition (%)',
    activiteFibId:                   null!,   // ID technique → masqué
  };

  // Champs séparés dans la carte (statut affiché à part)
  private separatedFields = ['febStatut'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { febId: string; febLabel?: string },
    public dialogRef: MatDialogRef<FebHistoryComponent>,
    private auditService: AuditService
  ) {
    this.febLabel = data.febLabel ?? data.febId;
    this.loadHistory();
  }

  loadHistory(): void {
    this.auditService.getAuditHistory('FEB', this.data.febId).subscribe({
      next: (logs: AuditLog[]) => {
        this.items = logs.map(log => this.toHistoryItem(log));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private parseJson(s: any): any {
    if (!s) return null;
    if (typeof s === 'string') {
      try { return JSON.parse(s); } catch { return null; }
    }
    return s;
  }

  private toHistoryItem(log: AuditLog): FebHistoryItem {
    const changedRaw = this.parseJson(log.changedFields);

    let allChanges: { field: string; before: any; after: any }[] = [];
    if (Array.isArray(changedRaw)) {
      allChanges = changedRaw.filter(c => c?.field);
    } else if (changedRaw && typeof changedRaw === 'object') {
      const before = this.parseJson(log.snapshotBefore) ?? {};
      const after  = this.parseJson(log.snapshotAfter)  ?? {};
      allChanges = Object.keys(changedRaw).map(f => ({
        field:  f,
        before: before?.[f],
        after:  after?.[f]
      }));
    }

    const statutChange = allChanges.find(c => c.field === 'febStatut');

    const changes: FieldChange[] = allChanges
      .filter(c => !this.separatedFields.includes(c.field))
      .map(c => {
        const label = this.humanizeField(c.field);
        if (label === null) return null; // champ technique masqué
        return {
          field:  c.field,
          label,
          before: this.formatValue(c.field, c.before),
          after:  this.formatValue(c.field, c.after),
        };
      })
      .filter((c): c is FieldChange => c !== null);

    return {
      log,
      date:        new Date(log.createdAt),
      projet:      (log as any).projet,
      statutAvant: statutChange?.before ?? undefined,
      statutApres: statutChange?.after  ?? undefined,
      changes,
    };
  }

  /**
   * Humanise un nom de champ technique en libellé lisible.
   * Retourne null pour les champs à masquer (IDs techniques, timestamps).
   */
  private humanizeField(field: string): string | null {
    // Champ connu directement
    if (Object.prototype.hasOwnProperty.call(this.fieldLabels, field)) {
      return this.fieldLabels[field] ?? null; // null = masqué
    }

    // Activités : febActivites[0].label → "Activité 1 — Label"
    const actMatch = field.match(/^febActivites\[(\d+)\]\.(.+)$/);
    if (actMatch) {
      const index = parseInt(actMatch[1]) + 1;
      const subField = actMatch[2];
      const subLabel = this.fieldLabels[subField];
      if (subLabel === null) return null;  // sous-champ masqué (activiteFibId)
      return `Activité ${index} — ${subLabel ?? subField}`;
    }

    // Champs imbriqués génériques avec id= : "[id=xxx].champ"
    const idMatch = field.match(/\[id=[^\]]+\]\.(.+)$/);
    if (idMatch) {
      const subField = idMatch[1];
      const subLabel = this.fieldLabels[subField];
      if (subLabel === null) return null;
      return subLabel ?? subField;
    }

    // Champ avec point : prefix.champ → on prend la feuille
    const dotIdx = field.lastIndexOf('.');
    if (dotIdx !== -1) {
      const leaf = field.substring(dotIdx + 1);
      const leafLabel = this.fieldLabels[leaf];
      if (leafLabel === null) return null;
      return leafLabel ?? leaf;
    }

    // Fallback : retourne le champ tel quel (visible mais brut)
    return field;
  }

  private formatValue(field: string, value: any): any {
    if (value === null || value === undefined || value === '') return null;

    // Booléen
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';

    // Durée → jours
    if (field === 'febDuree' || field.includes('Duree')) {
      const n = Number(value);
      if (!isNaN(n)) return n + ' j';
    }

    // Provision → euros
    if (field === 'febProvisionDeplacementAstreinte') {
      const n = Number(value);
      if (!isNaN(n)) return n.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';
    }

    // Pourcentage
    if (field === 'pourcentage' || field.includes('pourcentage')) {
      const n = Number(value);
      if (!isNaN(n)) return n + ' %';
    }

    // Dates ISO → format FR
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleDateString('fr-FR');
    }

    // Statut FEB → libellé français
    const statutLabels: Record<string, string> = {
      CREEE:      'Créée',
      EN_ATTENTE: 'En attente',
      VALIDEE:    'Validée',
      ARCHIVEE:   'Archivée',
    };
    if (typeof value === 'string' && statutLabels[value]) {
      return statutLabels[value];
    }

    return value;
  }

  // ── Helpers template ──────────────────────────────────────────────────────

  isStatusChange(item: FebHistoryItem): boolean {
    return !!item.statutApres && item.statutAvant !== item.statutApres;
  }

  operationLabel(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression'
    };
    return m[op] ?? op;
  }

  operationIcon(op: string): string {
    const m: Record<string, string> = {
      CREATE: 'add_circle', UPDATE: 'edit', DELETE: 'delete'
    };
    return m[op] ?? 'info';
  }

  operationColor(op: string): string {
    const m: Record<string, string> = {
      CREATE: '#2E7D32', UPDATE: '#1565C0', DELETE: '#E64A19'
    };
    return m[op] ?? '#616161';
  }

  statutColor(label?: string): string {
    if (!label) return '#616161';
    const map: Record<string, string> = {
      CREEE:      '#616161',
      EN_ATTENTE: '#1565C0',
      VALIDEE:    '#2E7D32',
      ARCHIVEE:   '#795548',
    };
    return map[label] ?? '#616161';
  }

  statutLabel(s?: string): string {
    if (!s) return '—';
    const map: Record<string, string> = {
      CREEE:      'Créée',
      EN_ATTENTE: 'En attente',
      VALIDEE:    'Validée',
      ARCHIVEE:   'Archivée',
    };
    return map[s] ?? s;
  }
}